
// src/views/Warehouse3D.jsx
import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useState
} from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
const API = import.meta.env.VITE_API_URL;
const API_WO = `${API}/api/warehouseobjects`;
/**
 * TYPE MAP (from locations):
 * 2 = RACK
 * 1 = FLOOR
 *
 * World frame:
 * - units = feet
 * - building centered: west wall (X = -L/2), east (X = +L/2),
 * south wall (Z = -W/2), north (Z = +W/2)
 *
 * Imperative API (ref):
 * focusLocation({ aisle, bay, shelf=1, bin=1 })
 */
export default forwardRef(function Warehouse3D(
  {
    rows = [],
    warehouseId,
    areaId,
    // Physical building dimensions (feet). Used to size the shell; camera can roam freely.
    dims = { lengthFt: 200, widthFt: 120, heightFt: 32 },
    // Where warehouseobjects coordinates come from:
    // - "lower-left" => x/z measured from SW corner (we center them)
    // - "centered" => already centered around 0,0
    origin = 'lower-left',
    // Restrict rendering to these aisles (array or Set of numbers). Omit for all.
    selectedAisles = null,
    // Pallet base options
    palletBaseMode = 'wire', // 'wire' | 'solid'
    showPalletBases = true // false hides pallet bases entirely
  },
  ref
) {
  // ---- scene wiring ----
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const racksRootRef = useRef(null);
  const ceilingMeshRef = useRef(null);
  const walkerMeshRef = useRef(null);
  const walkerAnimRef = useRef({ phase: 0, speed: 1 });
  const observerRef = useRef({ active: false });
  // render-on-demand
  const needsRenderRef = useRef(true);
  // camera fly-to state (driven from main loop)
  const isAnimatingRef = useRef(false);
  const animStateRef = useRef({
    start: 0,
    duration: 800,
    fromPos: new THREE.Vector3(),
    toPos: new THREE.Vector3(),
    fromTgt: new THREE.Vector3(),
    toTgt: new THREE.Vector3(),
    fromFov: 55,
    wideFov: 65,
    toFov: 55,
    hasZoomPop: true
  });
  function renderOnce() {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    r.render(s, c);
    needsRenderRef.current = false;
  }
  function invalidate() { needsRenderRef.current = true; }
  // live rows
  const rowsRef = useRef([]);
  rowsRef.current = Array.isArray(rows) ? rows : [];
  // design map
  const designMapRef = useRef(new Map());
  const [designTick, setDesignTick] = useState(0);
  // texture caches
  const textureBucketRef = useRef(new Set());
  const labelTextureCacheRef = useRef(new Map());
  const aisleFacingRef = useRef(new Map()); // aisle -> boolean (true => front toward -Z)
  // location index for focusing
  const locIndexRef = useRef(new Map());
  // Walking state
  // Initialize with a literal to avoid TDZ before constants are declared
  const walkRef = useRef({ active: false, curve: null, totalLen: 0, traveled: 0, speed: 20, eyeY: 5.5 }); // speed in ft/s (adaptive per walk)
  const lastFrameTimeRef = useRef(0);
  // ----------------------------------------------------------
  // constants / materials
  // ----------------------------------------------------------
  const feet = (ft) => ft; // world units == feet
  // Rack dims
  const RACK_W_FT = 8;
  const RACK_D_FT = 4;
  const RACK_H_FT = 24;
  const HALF_W = RACK_W_FT / 2;
  const HALF_D = RACK_D_FT / 2;
  // Beams
  const BEAM_START_FT = 5;
  const BEAM_SPACING_FT = 5;
  const BEAM_THICK_FT = 0.3; // Z thickness used for beams
  // 2×4s
  const TWO_BY_THICK_FT = 1.5 / 12;
  const TWO_BY_TALL_FT = 3.5 / 12;
  const BOARD_SPACING_FT = 24 / 12;
  const TWO_BY_EDGE_INSET = 0.15;
  const TWO_BY_Z_LEN = RACK_D_FT - 0.3;
  // Pallet + cargo
  const PALLET_W_FT = 40 / 12;
  const PALLET_D_FT = 48 / 12;
  const PALLET_H_FT = 4 / 12;
  const CARGO_H_FT = 3.0;
  const MIN_CARGO_H_FT = 1.0;
  const STACK_GAP_FT = 0.12;
  const PALLET_EDGE_CLEAR = 0.10;
  const PALLET_LABEL_W_FT = 2.4;
  const PALLET_LABEL_H_FT = 0.7;
  // Shell
  const CEILING_H_FT = Math.max(10, Number(dims?.heightFt ?? 32));
  // Camera offsets
  const WALK_OFFSET_FT = 10;
  const EYE_MIN_FT = 5.5;
  const WALK_EYE_FT = 6.2; // walking eye height
  const FLOOR_TARGET_Y_FT = 0.15; // ~2 inches
  const FLOOR_EYE_Y_FT = 0.20; // ~2.4 inches
  const TURN_CLEAR_FT = 3.0; // extra clearance past aisle end before turning
  const COLORS = {
    steel: 0x6ea0a3,
    beam: 0xff7a00,
    frame: 0x6ea0a3,
    text: '#ffffff',
    signBg: '#202020',
    grid1: 0x2a2a2a,
    grid2: 0x1a1a1a,
    floor: 0xaaaaaa,
    roof: 0x8c9094,
    wall: 0x3a3f46,
    wallEdge: 0x101214,
    oob: 0xff2a6d
  };
  const steelMat = new THREE.MeshStandardMaterial({ color: COLORS.steel, metalness: 0.2, roughness: 0.7 });
  const beamMat = new THREE.MeshStandardMaterial({ color: COLORS.beam, metalness: 0.1, roughness: 0.5 });
  const frameMat = new THREE.MeshStandardMaterial({ color: COLORS.frame, metalness: 0.2, roughness: 0.7 });
  const padMat = new THREE.MeshBasicMaterial({ color: 0x333333 });
  const palletDeckMat = new THREE.MeshStandardMaterial({ color: 0xa7825b, roughness: 0.9, metalness: 0.05 });
  const cargoBoxMat = new THREE.MeshStandardMaterial({
    color: 0xc9b089, roughness: 0.95, metalness: 0.02, transparent: false, opacity: 1,
    depthWrite: true, depthTest: true
  });
  // ----------------------------------------------------------
  // Instanced wireframe pallet bases (one InstancedMesh per rebuild)
  // ----------------------------------------------------------
  const THIN_BASE_T_FT = 0.5 / 12; // ultra-thin base
  const USE_INSTANCED_BASES = true; // toggle instancing
  const palletBaseGeoRef = useRef(
    new THREE.BoxGeometry(PALLET_W_FT, THIN_BASE_T_FT, PALLET_D_FT)
  );
  const palletBaseWireMat = useRef(
    new THREE.MeshBasicMaterial({
      color: 0x8a6e3b, wireframe: true, toneMapped: false, depthWrite: true
    })
  ).current;
  // Effective base thickness for vertical math
  const baseT = () => (showPalletBases
    ? (USE_INSTANCED_BASES ? THIN_BASE_T_FT : (palletBaseMode === 'wire' ? THIN_BASE_T_FT : PALLET_H_FT))
    : 0
  );
  // ----------------------------------------------------------
  // helpers
  // ----------------------------------------------------------
  function getAniso() {
    return rendererRef.current?.capabilities?.getMaxAnisotropy?.() ?? 8;
  }
  function easeInOutCubic(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  function disposeNode(obj) {
    if (!obj) return;
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) {
        obj.material.forEach(m => {
          if (m.map) m.map.dispose?.();
          m.dispose?.();
        });
      } else {
        if (obj.material.map) obj.material.map.dispose?.();
        obj.material.dispose?.();
      }
    }
  }
  function clearRacksRoot(scene) {
    if (!racksRootRef.current) return;
    const root = racksRootRef.current;
    scene.remove(root);
    root.traverse(disposeNode);
    textureBucketRef.current.forEach(t => t.dispose());
    textureBucketRef.current.clear();
    labelTextureCacheRef.current.clear();
    racksRootRef.current = null;
    locIndexRef.current.clear();
  }
  // ---------- signs & labels ----------
  function makeSign(text, widthFt = 5, heightFt = 2) {
    const dpi = 512;
    const c = document.createElement('canvas');
    c.width = dpi;
    c.height = Math.round(dpi * (heightFt / widthFt));
    const ctx = c.getContext('2d');
    ctx.fillStyle = COLORS.signBg;
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#888';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, c.width - 8, c.height - 8);
    const PAD_X = Math.round(c.width * 0.06);
    const PAD_Y = Math.round(c.height * 0.18);
    const targetW = c.width - PAD_X * 2;
    const targetH = c.height - PAD_Y * 2;
    ctx.fillStyle = COLORS.text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let fontPx = Math.floor(targetH * 0.60);
    const minPx = Math.max(10, Math.floor(targetH * 0.30));
    const label = String(text);
    while (fontPx > minPx) {
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      const w = ctx.measureText(label).width;
      if (w <= targetW && fontPx <= targetH) break;
      fontPx -= 2;
    }
    ctx.fillText(label, c.width / 2, c.height / 2);
    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = getAniso();
    textureBucketRef.current.add(tex);
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, side: THREE.FrontSide, alphaTest: 0.04,
      depthTest: true, depthWrite: true, polygonOffset: true,
      polygonOffsetFactor: -1, polygonOffsetUnits: -1
    });
    const geo = new THREE.PlaneGeometry(feet(widthFt), feet(heightFt));
    return new THREE.Mesh(geo, mat);
  }
  function tubeBetween(p1, p2, radius = 0.07, mat = frameMat) {
    const dir = new THREE.Vector3().subVectors(p2, p1);
    const len = dir.length();
    const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);
    const geom = new THREE.CylinderGeometry(radius, radius, len, 10, 1, false);
    const mesh = new THREE.Mesh(geom, mat);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    mesh.position.copy(mid);
    return mesh;
  }
  function makePalletLabel(text, widthFt = PALLET_LABEL_W_FT, heightFt = PALLET_LABEL_H_FT) {
    let raw = String(text ?? '');
    raw = raw.replace(/\r\n/g, '\n').replace(/\s*\|\s*/g, ' | ');
    raw = raw.replace(/(?<!^)\s+(Receipt\b)/ig, '\n$1');
    raw = raw.replace(/(?<!^)\s+(Pallet\b)/ig, '\n$1');
    raw = raw.replace(/(?<!^)\s+(Stack\b)/ig, '\n$1');
    raw = raw.replace(/(?<!^)\s+(Client\b)/ig, '\n$1');
    let parts = raw.split('\n').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 4) parts = [`${parts[0]} ${parts[1]}`, parts[2], parts[3]];
    const lines = parts.slice(0, 3);
    const key = `v4|${widthFt}|${heightFt}|${lines.join('␟')}`;
    const cached = labelTextureCacheRef.current.get(key);
    if (cached) return cached.clone();
    const PX = 512;
    const c = document.createElement('canvas');
    c.width = PX;
    c.height = Math.round(PX * (heightFt / widthFt));
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#222';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#888';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, c.width - 6, c.height - 6);
    const PAD_X = 22, PAD_Y = 14;
    const targetW = c.width - PAD_X * 2;
    const targetH = c.height - PAD_Y * 2;
    const lineGap = 1.18;
    let fontPx = Math.floor(Math.min(52, targetH / (lines.length + 0.15)));
    const minPx = 14;
    const fits = (size) => {
      ctx.font = `bold ${size}px system-ui, sans-serif`;
      return lines.every(ln => ctx.measureText(ln).width <= targetW);
    };
    while (fontPx > minPx && !fits(fontPx)) fontPx -= 2;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
    let y = PAD_Y;
    const step = Math.round(fontPx * lineGap);
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], PAD_X, y);
      y += step;
      if (y > c.height - PAD_Y) break;
    }
    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = getAniso();
    textureBucketRef.current.add(tex);
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, side: THREE.FrontSide, depthTest: true, depthWrite: true,
      alphaTest: 0.04, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1
    });
    const geo = new THREE.PlaneGeometry(widthFt, heightFt);
    const mesh = new THREE.Mesh(geo, mat);
    labelTextureCacheRef.current.set(key, mesh);
    return mesh.clone();
  }
  function makeVisualPallet({
    widthFt = PALLET_W_FT,
    depthFt = PALLET_D_FT,
    showCargo = false,
    labelText = '',
    cargoHeightFt = CARGO_H_FT,
    frontIsNegZ = true
  }) {
    const g = new THREE.Group();
    // OPTIONAL base: only if not using instanced bases
    if (showPalletBases && !USE_INSTANCED_BASES) {
      const deckT = (palletBaseMode === 'wire') ? THIN_BASE_T_FT : PALLET_H_FT;
      const geo = new THREE.BoxGeometry(widthFt, deckT, depthFt);
      const mat = (palletBaseMode === 'wire') ? palletBaseWireMat : palletDeckMat;
      const base = new THREE.Mesh(geo, mat);
      base.position.y = deckT / 2;
      base.matrixAutoUpdate = false; base.updateMatrix();
      g.add(base);
    }
    if (showCargo) {
      const h = Math.max(0.2, cargoHeightFt);
      const boxGeo = new THREE.BoxGeometry(widthFt * 0.98, h, depthFt * 0.98);
      const box = new THREE.Mesh(boxGeo, cargoBoxMat);
      box.position.y = baseT() + h / 2; // sit on surface (base/shelf/floor)
      g.add(box);
      // label — only when there is actual text to render
      if (String(labelText || '').trim()) {
        const LABEL_INSET_X = 0.04;
        const LABEL_INSET_Y = 0.02;
        const labelAspect = PALLET_LABEL_H_FT / PALLET_LABEL_W_FT;
        const labelW = Math.max(0.5, widthFt * 0.96);
        const naturalH = labelW * labelAspect;
        const maxH = Math.max(0.2, h * 0.45);
        const labelH = Math.min(naturalH, maxH);
        const label = makePalletLabel(labelText, labelW - LABEL_INSET_X * 2, labelH);
        const labelCenterY = baseT() + LABEL_INSET_Y + labelH / 2;
        const zOff = (frontIsNegZ ? -1 : +1) * (depthFt / 2 - 0.02);
        label.position.set(0, labelCenterY, zOff);
        label.rotation.y = frontIsNegZ ? Math.PI : 0;
        g.add(label);
      }
    }
    return g;
  }
  // Rack builder
  function buildRack(
    xWorld,
    zWorld,
    bayDepthFt = RACK_D_FT,
    bayWidthFt = RACK_W_FT,
    heightFt = RACK_H_FT,
    beamCount = 0,
    beamStartFt = BEAM_START_FT,
    beamSpacingFt = BEAM_SPACING_FT,
    yawRad = 0
  ) {
    const group = new THREE.Group();
    // uprights
    const postW = 0.3, postD = 0.3;
    const uprightH = feet(heightFt);
    const uprightGeo = new THREE.BoxGeometry(postW, uprightH, postD);
    const leftFront = new THREE.Mesh(uprightGeo, steelMat); leftFront.position.set(-HALF_W, uprightH / 2, -HALF_D);
    leftFront.name = 'upright-LF';
    const leftBack = leftFront.clone(); leftBack.position.set(-HALF_W, uprightH / 2, HALF_D);
    leftBack.name = 'upright-LB';
    const rightFront = leftFront.clone(); rightFront.position.set( HALF_W, uprightH / 2, -HALF_D);
    rightFront.name = 'upright-RF';
    const rightBack = leftFront.clone(); rightBack.position.set( HALF_W, uprightH / 2, HALF_D);
    rightBack.name = 'upright-RB';
    group.add(leftFront, leftBack, rightFront, rightBack);
    // beams
    const beamH = 0.25;
    const beamGeo = new THREE.BoxGeometry(bayWidthFt, beamH, BEAM_THICK_FT);
    const safeStart = Math.max(0, Number(beamStartFt) || 4);
    const safeStep = Math.max(0.1, Number(beamSpacingFt) || 5);
    for (let i = 0; i < beamCount; i++) {
      const lvl = safeStart + i * safeStep;
      if (lvl > heightFt) break;
      const y = feet(lvl);
      const front = new THREE.Mesh(beamGeo, beamMat); front.position.set(0, y, -HALF_D);
      const back = front.clone(); back.position.set(0, y, HALF_D);
      group.add(front, back);
    }
    group.rotation.y = yawRad;
    group.position.set(xWorld, 0, zWorld);
    return group;
  }
  // ----------------------------------------------------------
  // Parsing helpers (placards)
  // ----------------------------------------------------------
  function romanToInt(s = '') {
    const map = {I:1,V:5,X:10,L:50,C:100,D:500,M:1000};
    const S = (s || '').toUpperCase().replace(/[^IVXLCDM]/g,'');
    let total = 0, prev = 0;
    for (let i = S.length - 1; i >= 0; i--) {
      const cur = map[S[i]] || 0;
      total += (cur < prev) ? -cur : cur;
      prev = cur;
    }
    return total || NaN;
  }
  function stackOrderFromToken(tok) {
    const t = String(tok || '').trim().toUpperCase();
    if (!t) return 1;
    if (/^\d+$/.test(t)) return Math.max(1, parseInt(t, 10));
    if (/^[A-Z]$/.test(t)) return (t.charCodeAt(0) - 64);
    const r = romanToInt(t);
    if (Number.isFinite(r)) return Math.max(1, r);
    return 1;
  }
  function parsePlacards(pinfo) {
    const items = String(pinfo || '')
      .split(/\s*\|\s*/g)
      .map(s => s.trim())
      .filter(Boolean);
    const out = items.map(txt => {
      const mStack = txt.match(/stack\s*#?\s*([A-Za-z]+|\d+)/i);
      const token = mStack ? mStack[1] : 'A';
      const order = stackOrderFromToken(token);
      const mPallet = txt.match(/pallet\s*#?\s*(\d+)/i);
      const palletNum = mPallet ? parseInt(mPallet[1], 10) : Number.POSITIVE_INFINITY;
      return {
        stackKey: String(token).toUpperCase(),
        stackOrder: order,
        palletNum,
        label: txt
      };
    });
    out.sort((a, b) =>
      (a.stackOrder - b.stackOrder) ||
      (a.palletNum - b.palletNum) ||
      a.label.localeCompare(b.label)
    );
    return out;
  }
  function sortPlacards(list = []) {
    return [...list].sort((a, b) =>
      (a.stackOrder - b.stackOrder) ||
      (a.palletNum - b.palletNum) ||
      a.label.localeCompare(b.label)
    );
  }
  // ----------------------------------------------------------
  // Camera focus animation (driven from main loop)
  // ----------------------------------------------------------
  const vFromPos = new THREE.Vector3();
  const vToPos = new THREE.Vector3();
  const vFromTgt = new THREE.Vector3();
  const vToTgt = new THREE.Vector3();
  function computeShelfCenterY(shelf) {
    const s = Number(shelf);
    if (!Number.isFinite(s) || s <= 1) return baseT() / 2;
    const idx = s - 2; // shelf 2 -> first beam
    const beamYft = BEAM_START_FT + idx * BEAM_SPACING_FT;
    const y = feet(beamYft) + (0.25 / 2) + TWO_BY_TALL_FT + (baseT() / 2);
    return Math.min(y, feet(RACK_H_FT - 0.5));
  }
  function clampViewYOnly() {
    const cam = cameraRef.current;
    const ctr = controlsRef.current?.target;
    if (!cam || !ctr) return;
    // allow getting very close to the floor (y=0)
    const MIN_TARGET_Y = 0.05; // ~2 cm
    const MIN_EYE_Y = 0.12; // ~4 cm
    ctr.y = Math.max(MIN_TARGET_Y, ctr.y);
    cam.position.y = Math.max(MIN_EYE_Y, cam.position.y);
  }
  function focusLocation({ aisle, bay, shelf = 1, bin = null } = {}) {
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    const ctr = controls?.target;
    if (!cam || !controls || !ctr) return;
    const key = `${Number(aisle)}|${Number(bay)}`;
    const rec = locIndexRef.current.get(key);
    if (!rec) return;
    const { x, z, yawRad, shelfBins } = rec;
    // Choose aisle-facing for front/back offset (prefer persisted meta)
    const aNum = Number(aisle);
    const facing = aisleFacingRef.current?.get?.(aNum);
    const frontIsNegZ = (typeof facing === 'boolean') ? facing : true;
    const frontSign = frontIsNegZ ? 1 : -1;
    // lateral offset across rack width
    let localX = 0;
    if (bin != null && shelfBins instanceof Map && shelfBins.has(Number(shelf))) {
      const binsSet = shelfBins.get(Number(shelf));
      const sorted = [...binsSet].sort((a, b) => a - b);
      const n = Math.max(1, sorted.length);
      const segment = (2 * HALF_W) / n;
      const centers = Array.from({ length: n }, (_, i) => -HALF_W + segment * (i + 0.5));
      const centersRev = centers.slice().reverse();
      const idx = Math.max(0, sorted.indexOf(Number(bin)));
      localX = centersRev[idx] ?? 0;
    }
    const yAxis = new THREE.Vector3(0,1,0);
    const front = new THREE.Vector3(0, 0, -1).applyAxisAngle(yAxis, yawRad);
    const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(yAxis, yawRad);
    const isFloorShelf = Number(shelf ?? 1) <= 1;
    const rawTargetY = isFloorShelf ? FLOOR_TARGET_Y_FT : computeShelfCenterY(shelf ?? 1);
    const minTargetClamp = (isFloorShelf ? FLOOR_EYE_Y_FT : EYE_MIN_FT) - 1.0;
    const targetY = Math.max(0.8, Math.max(minTargetClamp, rawTargetY));
    const baseMinEye = isFloorShelf ? FLOOR_EYE_Y_FT : EYE_MIN_FT;
    const eyeY = Math.max(baseMinEye, targetY + 1.25);
    const face = new THREE.Vector3(x, targetY, z)
      .addScaledVector(front, frontSign * (HALF_D - 0.02))
      .addScaledVector(right, localX);
    const pos = new THREE.Vector3(x, eyeY, z)
      .addScaledVector(front, frontSign * (HALF_D + WALK_OFFSET_FT))
      .addScaledVector(right, localX);
    // prime animation
    animStateRef.current.start = performance.now();
    animStateRef.current.duration = 1200;
    animStateRef.current.fromPos.copy(cam.position);
    animStateRef.current.toPos.copy(pos);
    animStateRef.current.fromTgt.copy(ctr);
    animStateRef.current.toTgt.copy(face);
    // FOV: zoom out then in
    animStateRef.current.fromFov = cam.fov;
    animStateRef.current.wideFov = cam.fov + 30;
    animStateRef.current.toFov = 48;
    animStateRef.current.hasZoomPop = true;
    controls.enabled = false;
    isAnimatingRef.current = true;
    invalidate();
  }
  // Start a walk animation that turns onto the aisle, runs along it,
  // then turns into the requested bay and faces the rack front.
  function walkToLocation({ aisle, bay, shelf = 1, bin = null } = {}) {
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    const ctr = controls?.target;
    if (!cam || !controls || !ctr) return;
    const key = `${Number(aisle)}|${Number(bay)}`;
    const rec = locIndexRef.current.get(key);
    if (!rec) return;
    // Determine aisle front/back relative to this rack and compute axes
    const aNum = Number(aisle);
    const facing = aisleFacingRef.current?.get?.(aNum);
    const frontIsNegZ = (typeof facing === 'boolean') ? facing : true;
    const frontSign = frontIsNegZ ? 1 : -1;
    const yAxis = new THREE.Vector3(0,1,0);
    const yawRad = rec.yawRad || 0;
    const rackFront = new THREE.Vector3(0,0,-1).applyAxisAngle(yAxis, yawRad).multiplyScalar(frontSign);
    const rackRight = new THREE.Vector3(1,0,0).applyAxisAngle(yAxis, yawRad);
    // Lateral bin center offset across rack face (if known)
    let localX = 0;
    const shelfBins = rec.shelfBins;
    if (bin != null && shelfBins instanceof Map && shelfBins.has(Number(shelf))) {
      const binsSet = shelfBins.get(Number(shelf));
      const sorted = [...binsSet].sort((a, b) => a - b);
      const n = Math.max(1, sorted.length);
      const segment = (2 * HALF_W) / n;
      const centers = Array.from({ length: n }, (_, i) => -HALF_W + segment * (i + 0.5));
      const centersRev = centers.slice().reverse();
      const idx = Math.max(0, sorted.indexOf(Number(bin)));
      localX = centersRev[idx] ?? 0;
    }
    // Choose look/eye heights similar to focusLocation
    const isFloorShelf = Number(shelf ?? 1) <= 1;
    const targetY = isFloorShelf ? FLOOR_TARGET_Y_FT : computeShelfCenterY(shelf ?? 1);
    // Keep walking eye height at a slightly taller 6.0 ft throughout the walk
    const eyeYWalk = WALK_EYE_FT;
    // Compute the face point (center of requested bin on the rack front) and final focus pose
    const rackCenter = new THREE.Vector3(rec.x, 0, rec.z);
    const facePoint = rackCenter.clone()
      .addScaledVector(rackRight, localX)
      .addScaledVector(rackFront, (HALF_D - 0.02));
    const faceTarget = facePoint.clone();
    faceTarget.y = targetY;
    // End camera position: small standoff from the face along front (in the aisle)
    const standoff = 2.6;
    const endPos = facePoint.clone().addScaledVector(rackFront, +standoff);
    // Final focus position like focusLocation (further back, comfortable view)
    const finalPos = rackCenter.clone()
      .addScaledVector(rackFront, (HALF_D + WALK_OFFSET_FT) * 1) // same sign as focusLocation
      .addScaledVector(rackRight, localX);
    finalPos.y = eyeYWalk;
    // Start from current camera XZ for responsiveness
    const camStart = cameraRef.current.position;
    const start = new THREE.Vector3(camStart.x, 0, camStart.z);
    // Aisle center line a few feet out from the rack face (target aisle)
    const AISLE_HALF_W = 6.0; // center lane in front of racks
    const targetAisleCL = rackCenter.clone().addScaledVector(rackFront, HALF_D + AISLE_HALF_W);
    const targetAisleAtBin = targetAisleCL.clone().addScaledVector(rackRight, localX);
    // Build corridor-aware route: stay in current aisle to an end, cross the corridor,
    // enter the target aisle, then approach the bay.
    const L = Math.max(10, Number(dims?.lengthFt ?? 200));
    const W = Math.max(10, Number(dims?.widthFt ?? 120));
    const toWorld = ({ xFt, zFt }) => {
      if ((origin || '').toLowerCase() === 'centered') return { x: xFt, z: zFt };
      return { x: xFt - L / 2, z: zFt - W / 2 };
    };
    // Build minimal aisle geometry from design
    const aislesMap = new Map(); // aisle -> { p0, right, front, endA, endB, aisleNum }
    try {
      const grouped = new Map();
      designMapRef.current.forEach((val, key) => {
        const [aStr, bStr] = key.split('|');
        const a = Number(aStr), b = Number(bStr);
        if (!Number.isFinite(a) || !Number.isFinite(b)) return;
        if (!grouped.has(a)) grouped.set(a, []);
        grouped.get(a).push({ bay: b, ...val });
      });
      for (const [a, bays] of grouped.entries()) {
        const sorted = bays.sort((p,q)=>p.bay-q.bay);
        const sample = sorted[0];
        if (!sample) continue;
        const yaw = THREE.MathUtils.degToRad(sample.oriDeg || sample.orientation_deg || 0);
        const frontSignAisle = (typeof (aisleFacingRef.current?.get?.(a)) === 'boolean')
          ? (aisleFacingRef.current.get(a) ? 1 : -1) : 1;
        const frontA = new THREE.Vector3(0,0,-1).applyAxisAngle(new THREE.Vector3(0,1,0), yaw).multiplyScalar(frontSignAisle);
        const rightA = new THREE.Vector3(1,0,0).applyAxisAngle(new THREE.Vector3(0,1,0), yaw);
        // Compute aisle center points for each bay
        const aislePoints = sorted.map(b => {
          const { x, z } = toWorld({ xFt: b.xFt, zFt: b.zFt });
          const rackC = new THREE.Vector3(x,0,z);
          return rackC.clone().addScaledVector(frontA, HALF_D + AISLE_HALF_W);
        });
        if (aislePoints.length === 0) continue;
        // Reference point on the aisle centerline: average of points
        const pRef = aislePoints.reduce((acc, p) => acc.add(p.clone()), new THREE.Vector3()).multiplyScalar(1/aislePoints.length);
        // Scalar coordinates along the aisle axis relative to pRef
        let minT = Infinity, maxT = -Infinity;
        for (const p of aislePoints) {
          const t = rightA.dot(p.clone().sub(pRef));
          if (t < minT) minT = t;
          if (t > maxT) maxT = t;
        }
        // Choose p0 as the midpoint along the aisle span for stable projection
        const midT = (minT + maxT) / 2;
        const p0 = pRef.clone().addScaledVector(rightA, midT);
        const endA = pRef.clone().addScaledVector(rightA, minT);
        const endB = pRef.clone().addScaledVector(rightA, maxT);
        aislesMap.set(a, { p0, right: rightA, front: frontA, endA, endB, aisleNum: a });
      }
    } catch {}
    // Determine current aisle by nearest aisle centerline
    let currGeom = null;
    if (aislesMap.size > 0) {
      const startXZ = new THREE.Vector3(start.x, 0, start.z);
      let bestD = Infinity; let bestGeom = null;
      for (const [, g] of aislesMap.entries()) {
        const v = startXZ.clone().sub(g.p0);
        const along = g.right.clone().multiplyScalar(g.right.dot(v));
        const perp = v.sub(along);
        const d = perp.length();
        if (d < bestD) { bestD = d; bestGeom = g; }
      }
      currGeom = bestGeom;
    }
    const pts = [];
    pts.push(start.clone());
    const targetGeom = aislesMap.get(aNum) || null;
    if (currGeom) {
      // Project start to current aisle centerline
      const v = new THREE.Vector3(start.x,0,start.z).sub(currGeom.p0);
      const startProj = currGeom.p0.clone().add(currGeom.right.clone().multiplyScalar(currGeom.right.dot(v)));
      pts.push(startProj.clone());
      // Determine align point on target aisle centerline for this bin
      const alignOnTarget = (targetGeom)
        ? (() => {
            const v2 = targetAisleAtBin.clone().sub(targetGeom.p0);
            const t = targetGeom.right.dot(v2);
            return targetGeom.p0.clone().addScaledVector(targetGeom.right, t);
          })()
        : targetAisleAtBin.clone();
      const currAisle = currGeom.aisleNum;
      if (currAisle === aNum) {
        // Same aisle: direct along centerline to align
        pts.push(alignOnTarget.clone());
      } else {
        // Different aisle: exit current, cross, enter target
        // Choose which end of current aisle to exit by minimizing total manhattan-like route
        const ends = [currGeom.endA.clone(), currGeom.endB.clone()];
        let bestExit = ends[0];
        let bestCost = Infinity;
        for (const end of ends) {
          const alongCurr = Math.abs(currGeom.right.dot(end.clone().sub(startProj)));
          // corridor distance along current aisle front to reach target aisle line
          const delta = currGeom.front.dot((targetGeom ? targetGeom.p0 : targetAisleCL).clone().sub(end));
          const cross = Math.abs(delta);
          const crossDest = end.clone().addScaledVector(currGeom.front, delta);
          // along target aisle to align
          const alongTgt = targetGeom
            ? Math.abs(targetGeom.right.dot(alignOnTarget.clone().sub(crossDest)))
            : Math.abs(rackRight.dot(alignOnTarget.clone().sub(crossDest)));
          const cost = alongCurr + cross + alongTgt;
          if (cost < bestCost) { bestCost = cost; bestExit = end.clone(); }
        }
        // segment 1: move along the current aisle centerline from startProj toward the exit (no sideways from the aisle)
        const alongToExit = currGeom.right.dot(bestExit.clone().sub(startProj));
        const toExitAlong = startProj.clone().add(currGeom.right.clone().multiplyScalar(alongToExit));
        // go slightly beyond the aisle end before turning (clear the edge)
        const outSign = (bestExit.distanceTo(currGeom.endB) < 1e-6) ? +1 : (bestExit.distanceTo(currGeom.endA) < 1e-6 ? -1 : Math.sign(alongToExit) || 1);
        const exitBeyond = toExitAlong.clone().add(currGeom.right.clone().multiplyScalar(outSign * TURN_CLEAR_FT));
        pts.push(exitBeyond.clone());
        // segment 2: cross corridor along current aisle's front (pure straight)
        const delta = currGeom.front.dot((targetGeom ? targetGeom.p0 : targetAisleCL).clone().sub(exitBeyond));
        const crossDest = exitBeyond.clone().addScaledVector(currGeom.front, delta);
        pts.push(crossDest.clone());
        // segment 3: continue along corridor to nearest target-aisle entrance (endA or endB)
        let targetEntrance = targetAisleCL.clone();
        if (targetGeom) {
          const dEA = crossDest.distanceTo(targetGeom.endA);
          const dEB = crossDest.distanceTo(targetGeom.endB);
          targetEntrance = (dEA <= dEB) ? targetGeom.endA.clone() : targetGeom.endB.clone();
        }
        pts.push(targetEntrance.clone());
        // segment 4: along target aisle centerline to alignOnTarget (pure straight)
        pts.push(alignOnTarget.clone());
      }
    } else {
      // Fallback simple route within target aisle
      const toStart = start.clone().sub(targetAisleCL);
      const frontDelta = rackFront.dot(toStart);
      const mid1 = start.clone().addScaledVector(rackFront, -frontDelta);
      const alongRightDelta = rackRight.dot(targetAisleAtBin.clone().sub(mid1));
      const mid2 = mid1.clone().addScaledVector(rackRight, alongRightDelta);
      pts.push(mid1, mid2);
    }
    // segment 5: final approach straight into bay standoff
    const approach = new THREE.Vector3(endPos.x, 0, endPos.z);
    pts.push(approach);
    // Create smooth spline path
    if (pts.length < 2) return;
    const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.1); // low tension for sharper curves
    const totalLen = curve.getLength();
    // Adaptive speed: aim for ~3s travel, min 8 ft/s, cap 70 ft/s
    const targetSec = 3.0;
    const speed = Math.min(70, Math.max(8, totalLen / targetSec));
    // reset frame timer for smooth first step
    lastFrameTimeRef.current = 0;
    // show walker indicator if observing
    if (walkerMeshRef.current) {
      walkerMeshRef.current.visible = !!observerRef.current.active;
      walkerMeshRef.current.position.set(start.x, 0, start.z);
    }
    walkRef.current = {
      active: true,
      curve,
      totalLen,
      traveled: 0,
      speed,
      eyeY: eyeYWalk,
      finalTarget: faceTarget,
      finalPos,
      currXZ: start.clone()
    };
    controls.enabled = false;
    invalidate();
  }
  useImperativeHandle(ref, () => ({
    focusLocation,
    walkToLocation,
    setObserverMode(on) { observerRef.current.active = !!on; invalidate(); }
  }));
  // ----------------------------------------------------------
  // Simple fetch helper for design
  // ----------------------------------------------------------
  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { msg = (await res.json())?.error || msg; } catch {}
      throw new Error(msg);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  };
  // Track if we've attempted auto-facing backfill to avoid loops
  const facingInitTriedRef = useRef(false);
  // Load design for selected warehouse/area
  useEffect(() => {
    if (!warehouseId || !areaId) {
      designMapRef.current = new Map();
      setDesignTick(t => t + 1);
      return;
    }
    (async () => {
      try {
        const url = `${API_WO}/design?warehouseid=${encodeURIComponent(warehouseId)}&areaid=${encodeURIComponent(areaId)}`;
        const list = await fetchJSON(url) || [];
        const map = new Map();
        const facingMap = new Map();
        for (const r of list) {
          const a = Number(r.roww), b = Number(r.columnn);
          if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
          const xFt = Number(r.x_ft) || 0;
          const zFt = Number(r.z_ft) || 0;
          const ori = ((Number(r.orientation_deg) || 0) % 360 + 360) % 360;
          map.set(`${a}|${b}`, { xFt, zFt, oriDeg: ori });
          // parse persisted facing from meta once per aisle
          try {
            const meta = r.meta && (typeof r.meta === 'string' ? JSON.parse(r.meta) : r.meta);
            if (meta && typeof meta.facing === 'boolean' && !facingMap.has(a)) facingMap.set(a, !!meta.facing);
          } catch {}
        }
        designMapRef.current = map;
        aisleFacingRef.current = facingMap;
        // If no facing persisted yet, attempt one-time backfill then reload
        if (facingMap.size === 0 && !facingInitTriedRef.current) {
          facingInitTriedRef.current = true;
          try {
            await fetch(`${API_WO}/infer-facing`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ warehouseid: Number(warehouseId), areaid: Number(areaId) })
            });
            // reload after backfill
            const list2 = await fetchJSON(url) || [];
            const map2 = new Map();
            const facingMap2 = new Map();
            for (const r of list2) {
              const a = Number(r.roww), b = Number(r.columnn);
              if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
              const xFt = Number(r.x_ft) || 0;
              const zFt = Number(r.z_ft) || 0;
              const ori = ((Number(r.orientation_deg) || 0) % 360 + 360) % 360;
              map2.set(`${a}|${b}`, { xFt, zFt, oriDeg: ori });
              try {
                const meta = r.meta && (typeof r.meta === 'string' ? JSON.parse(r.meta) : r.meta);
                if (meta && typeof meta.facing === 'boolean' && !facingMap2.has(a)) facingMap2.set(a, !!meta.facing);
              } catch {}
            }
            designMapRef.current = map2;
            aisleFacingRef.current = facingMap2;
          } catch (e2) {
            console.warn('Warehouse3D: infer-facing backfill failed:', e2?.message || e2);
          }
        }
      } catch (e) {
        console.error('Warehouse3D: design fetch failed', e);
        designMapRef.current = new Map();
      } finally {
        setDesignTick(t => t + 1);
      }
    })();
  }, [warehouseId, areaId]);
  // ----------------------------------------------------------
  // Utility: choose cargo height so n stacks fit under the shelf above
  // ----------------------------------------------------------
  function chooseCargoHeight({ shelfNum, nStacks, yCenter, beamLevels }) {
    if (nStacks <= 1) return CARGO_H_FT;
    let topCap;
    if (shelfNum <= 1) {
      topCap = feet(BEAM_START_FT) - 0.25;
    } else {
      const levelIdx = shelfNum - 2;
      if (levelIdx + 1 < beamLevels.length) {
        const nextBeamFt = beamLevels[levelIdx + 1];
        topCap = feet(nextBeamFt) - 0.35;
      } else {
        topCap = feet(RACK_H_FT) - 0.6;
      }
    }
    const allowance = topCap - yCenter - (nStacks * baseT()) - ((nStacks - 1) * STACK_GAP_FT) - 0.05;
    const perStackMax = allowance / nStacks;
    if (!Number.isFinite(perStackMax)) return CARGO_H_FT;
    return Math.max(MIN_CARGO_H_FT, Math.min(CARGO_H_FT, perStackMax));
  }
  // ----------------------------------------------------------
  // Warehouse shell (plain floor, optional grid/walls)
  // ----------------------------------------------------------
  function rebuildShell() {
    const scene = sceneRef.current;
    if (!scene) return;
    const old = scene.getObjectByName('warehouse-shell');
    if (old) {
      scene.remove(old);
      old.traverse(disposeNode);
    }
    const g = new THREE.Group();
    g.name = 'warehouse-shell';
    const L = Math.max(10, Number(dims?.lengthFt ?? 200));
    const W = Math.max(10, Number(dims?.widthFt ?? 120));
    // Floor slab (plain color)
    {
      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(L, W),
        new THREE.MeshBasicMaterial({ color: COLORS.floor })
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = -0.02;
      floor.renderOrder = -1;
      floor.name = 'shell-floor';
      g.add(floor);
    }
    // Walls (thin planes) + outline
    {
      const wallMat = new THREE.MeshBasicMaterial({
        color: COLORS.wall,
        transparent: true,
        opacity: 0.92,
        depthWrite: true
      });
      // West/East walls
      const wallXGeo = new THREE.PlaneGeometry(L, CEILING_H_FT);
      const west = new THREE.Mesh(wallXGeo, wallMat.clone());
      west.position.set(0, CEILING_H_FT / 2, -W / 2);
      g.add(west);
      const east = new THREE.Mesh(wallXGeo, wallMat.clone());
      east.position.set(0, CEILING_H_FT / 2, W / 2);
      east.rotation.y = Math.PI;
      g.add(east);
      // South/North walls
      const wallZGeo = new THREE.PlaneGeometry(W, CEILING_H_FT);
      const south = new THREE.Mesh(wallZGeo, wallMat.clone());
      south.position.set(-L / 2, CEILING_H_FT / 2, 0);
      south.rotation.y = -Math.PI / 2;
      g.add(south);
      const north = new THREE.Mesh(wallZGeo, wallMat.clone());
      north.position.set( L / 2, CEILING_H_FT / 2, 0);
      north.rotation.y = Math.PI / 2;
      g.add(north);
      const edgeGeo = new THREE.EdgesGeometry(new THREE.BoxGeometry(L, CEILING_H_FT, W));
      const edgeMat = new THREE.LineBasicMaterial({ color: COLORS.wallEdge, linewidth: 1 });
      const edges = new THREE.LineSegments(edgeGeo, edgeMat);
      edges.position.set(0, CEILING_H_FT / 2, 0);
      g.add(edges);
    }
     //(Optional) very light grid — comment this block to remove completely
     {
       const size = Math.max(L, W);
       const divisions = Math.max(1, Math.round(size / 20));
       const grid = new THREE.GridHelper(size, divisions, COLORS.grid1, COLORS.grid2);
       grid.position.y = 0.0;
       grid.name = 'shell-grid';
       g.add(grid);
     }
    scene.add(g);
  }
  // ----------------------------------------------------------
  // REBUILD SCENE (racks, pallets) + Instanced bases
  // ----------------------------------------------------------
  const selectedAisleSet = useMemo(() => {
    if (!selectedAisles) return null;
    const arr = Array.isArray(selectedAisles)
      ? selectedAisles
      : (selectedAisles instanceof Set ? Array.from(selectedAisles) : []);
    const nums = arr.map(Number).filter(Number.isFinite);
    return nums.length ? new Set(nums) : null;
  }, [selectedAisles]);
  function rebuildScene() {
    const scene = sceneRef.current;
    if (!scene) return;
    // reset content root
    clearRacksRoot(scene);
    const root = new THREE.Group();
    racksRootRef.current = root;
    scene.add(root);
    // Instanced wireframe bases
    let baseInst = null, baseIdx = 0;
    if (showPalletBases && USE_INSTANCED_BASES) {
      const cap = Math.max(4000, (rowsRef.current?.length || 0) * 4);
      baseInst = new THREE.InstancedMesh(palletBaseGeoRef.current, palletBaseWireMat, cap);
      baseInst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      root.add(baseInst);
    }
    const _m4 = new THREE.Matrix4();
    const _qY = new THREE.Quaternion();
    const _s = new THREE.Vector3();
    function placeBaseInstance({ worldX, worldY, worldZ, yawRad, xScale }) {
      if (!baseInst) return;
      _qY.setFromAxisAngle(new THREE.Vector3(0,1,0), yawRad);
      _s.set(xScale, 1, 1);
      _m4.compose(new THREE.Vector3(worldX, worldY, worldZ), _qY, _s);
      if (baseIdx < baseInst.count) baseInst.setMatrixAt(baseIdx, _m4);
      baseIdx++;
    }
    const data = rowsRef.current;
    if (!Array.isArray(data) || data.length === 0) {
      renderDesignOnly(root);
      return;
    }
    const L = Math.max(10, Number(dims?.lengthFt ?? 200));
    const W = Math.max(10, Number(dims?.widthFt ?? 120));
    const toWorld = ({ xFt, zFt }) => {
      if ((origin || '').toLowerCase() === 'centered') return { x: xFt, z: zFt };
      return { x: xFt - L / 2, z: zFt - W / 2 };
    };
    // group rows: aisle -> bay
    const aisleMap = new Map();
    data.forEach(r => {
      const aisle = Number(r.roww);
      const bay = Number(r.columnn);
      const shelf = Number(r.shelve);
      const bin = Number(r.bin);
      const type = Number(r.type); // 2=rack, 1=floor, 6=picking bin
      const pinfo = (r.palletInfo ?? r.palletinfo ?? '').toString().trim();
      if (!Number.isFinite(aisle) || !Number.isFinite(bay)) return;
      if (selectedAisleSet && !selectedAisleSet.has(aisle)) return;
      if (!aisleMap.has(aisle)) aisleMap.set(aisle, new Map());
      const bayMap = aisleMap.get(aisle);
      if (!bayMap.has(bay)) {
        bayMap.set(bay, {
          type: 1,
          shelves: new Set(),
          shelfBins: new Map(),
          shelfBinPlacards: new Map(),
          shelfBinSkuInfo: new Map(),
          clients: new Map(),
          locationLabels: new Map(),
          isPicking: false
        });
      }
      const entry = bayMap.get(bay);
      if (Number.isFinite(shelf)) {
        entry.shelves.add(shelf || 1);
        if (!entry.shelfBins.has(shelf)) entry.shelfBins.set(shelf, new Set());
        if (Number.isFinite(bin)) entry.shelfBins.get(shelf).add(bin);
        if (pinfo) {
          const placards = parsePlacards(pinfo);
          if (!entry.shelfBinPlacards.has(shelf)) entry.shelfBinPlacards.set(shelf, new Map());
          const perBin = entry.shelfBinPlacards.get(shelf);
          const key = Number.isFinite(bin) ? bin : 1;
          const prev = perBin.get(key) || [];
          const seen = new Set(prev.map(o => o.label));
          const merged = prev.concat(placards.filter(o => !seen.has(o.label)));
          perBin.set(key, sortPlacards(merged));
        }
        // Capture primary SKU and pieces for this exact shelf/bin for bin labels
        const skusStr = (r.skus ?? '').toString().trim();
        const skuPiecesStr = (r.sku_pieces ?? '').toString().trim();
        if (skusStr || skuPiecesStr) {
          if (!entry.shelfBinSkuInfo) entry.shelfBinSkuInfo = new Map();
          if (!entry.shelfBinSkuInfo.has(shelf)) entry.shelfBinSkuInfo.set(shelf, new Map());
          const perBinInfo = entry.shelfBinSkuInfo.get(shelf);
          const key = Number.isFinite(bin) ? bin : 1;
          let primarySku = '';
          if (skusStr) primarySku = skusStr.split(',')[0].trim();
          let piecesMap = new Map();
          if (skuPiecesStr) {
            skuPiecesStr.split('|').forEach(pair => {
              const [s, q] = (pair || '').split(':');
              const qq = Number(q);
              if (s && Number.isFinite(qq)) piecesMap.set(s.trim(), qq);
            });
          }
          const pieces = primarySku && piecesMap.has(primarySku)
            ? piecesMap.get(primarySku)
            : (piecesMap.size ? Array.from(piecesMap.values())[0] : null);
          // Store as { sku, pieces } so downstream label code can read 'pieces'
          perBinInfo.set(key, { sku: primarySku || (skusStr ? skusStr.split(',')[0].trim() : ''), pieces });
        }

        // Capture clients and location labels per shelf/bin
        const cliStr = (r.clients ?? '').toString().trim();
        const locLabel = (r.locationlabel ?? '').toString().trim();
        if (cliStr) {
          if (!entry.clients.has(shelf)) entry.clients.set(shelf, new Map());
          const perBinClients = entry.clients.get(shelf);
          const key = Number.isFinite(bin) ? bin : 1;
          perBinClients.set(key, cliStr);
        }
        if (locLabel) {
          if (!entry.locationLabels.has(shelf)) entry.locationLabels.set(shelf, new Map());
          const perBinLoc = entry.locationLabels.get(shelf);
          const key = Number.isFinite(bin) ? bin : 1;
          perBinLoc.set(key, locLabel);
        }
      }
      // Treat rack/picking as rack-type for visualization; mark picking for type 3 or 6
      if (type === 2 || type === 3 || type === 6) entry.type = 2;
      if (type === 3 || type === 6) entry.isPicking = true;
    });
    const sortedAisles = [...aisleMap.keys()].sort((a, b) => a - b);
    const yAxis = new THREE.Vector3(0,1,0);
    const aisleSignMounted = new Set();
    // helper: centers across width (rack-local), bin 1 leftmost as viewed
    const centersFor = (n) => {
      const N = Math.max(1, n);
      const seg = (2 * HALF_W) / N;
      const cx = [];
      for (let i = 0; i < N; i++) cx.push(-HALF_W + seg * (i + 0.5));
      // Left-to-right (facing front) should map increasing bin index → increasing local X
      return cx;
    };
    sortedAisles.forEach(aisle => {
      const bayMap = aisleMap.get(aisle);
      const sortedBays = [...bayMap.keys()].sort((a, b) => a - b);
      // Determine aisle facing: prefer persisted meta; otherwise infer from rack yaw
      const persistedFacing = aisleFacingRef.current?.get?.(aisle);
      let frontIsNegZ;
      if (typeof persistedFacing === 'boolean') {
        frontIsNegZ = persistedFacing;
      } else if (designMapRef.current.size) {
        // Infer from the first available bay in this aisle
        const sampleBay = [...bayMap.keys()][0];
        const pSamp = designMapRef.current.get(`${aisle}|${sampleBay}`);
        if (pSamp) {
          const yawSamp = THREE.MathUtils.degToRad(((pSamp.oriDeg || 0) + 180));
          const frontSamp = new THREE.Vector3(0,0,-1).applyAxisAngle(yAxis, yawSamp);
          // If the front vector points closer to world -Z than +Z, treat as frontIsNegZ=true
          const dotNegZ = frontSamp.dot(new THREE.Vector3(0,0,-1));
          const dotPosZ = frontSamp.dot(new THREE.Vector3(0,0, 1));
          frontIsNegZ = dotNegZ >= dotPosZ;
        } else {
          frontIsNegZ = true; // fallback
        }
      } else {
        frontIsNegZ = true;
      }
      // Decide if this aisle's visual bay numbering should be flipped based on bay
      // world positions relative to the viewer's right (when front-facing the aisle).
      const minBayNum = Math.min(...sortedBays);
      const maxBayNum = Math.max(...sortedBays);
      const pMin = designMapRef.current.get(`${aisle}|${minBayNum}`);
      const pMax = designMapRef.current.get(`${aisle}|${maxBayNum}`);
      let flipNumbering = false;
      if (pMin && pMax) {
        // Derive FRONT from rack yaw and RIGHT = up × front (viewer facing logic)
        const yawMin = THREE.MathUtils.degToRad(((pMin.oriDeg || 0) + 180));
        const frontVec = new THREE.Vector3(0,0,-1).applyAxisAngle(yAxis, yawMin).normalize();
        const rightVec = new THREE.Vector3().crossVectors(new THREE.Vector3(0,1,0), frontVec).normalize();
        const wMin = toWorld({ xFt: pMin.xFt, zFt: pMin.zFt });
        const wMax = toWorld({ xFt: pMax.xFt, zFt: pMax.zFt });
        const delta = new THREE.Vector3(wMax.x - wMin.x, 0, wMax.z - wMin.z);
        const dot = delta.x * rightVec.x + delta.z * rightVec.z;
        // If max bay lies to the left of min along RIGHT axis (dot < 0), flip numbering
        flipNumbering = dot < 0;
      }
      sortedBays.forEach(bay => {
        // Do NOT remap contents; contents stay with their own bay.
        // Only labels may flip for left→right visual numbering when facing the aisle.
        const entry = bayMap.get(bay);
        const pos = designMapRef.current.get(`${aisle}|${bay}`);
        if (!pos) return;
        const { x, z } = toWorld({ xFt: pos.xFt, zFt: pos.zFt });
        const yawRad = THREE.MathUtils.degToRad((pos.oriDeg || 0) + 180);
        const frontW = new THREE.Vector3(0,0,-1).applyAxisAngle(yAxis, yawRad);
        const rightW = new THREE.Vector3(1,0, 0).applyAxisAngle(yAxis, yawRad);
        const shelfCenters = new Map();
        const shelfY = new Map();
        locIndexRef.current.set(`${aisle}|${bay}`, {
          x, z, yawRad, type: entry.type, shelfBins: entry.shelfBins, shelfCenters, shelfY
        });
        if (entry.type !== 2) {
          // ---- FLOOR PAD ONLY ----
          const pad = new THREE.Mesh(new THREE.PlaneGeometry(8, 10), padMat);
          pad.rotation.x = -Math.PI / 2;
          pad.position.set(x, 0.01, z);
          root.add(pad);
          // floor shelf (1)
          const binsSet = entry.shelfBins.get(1) || new Set([1]);
          const sortedBins = [...binsSet].sort((a, b) => a - b);
          const centersX = centersFor(sortedBins.length || 1);
          shelfCenters.set(1, { bins: sortedBins.length ? sortedBins : [1], centersX });
          const yCenter = baseT() / 2; // center of base if present; else 0
          shelfY.set(1, yCenter);
          const placardMap = entry.shelfBinPlacards.get(1) || new Map();
          const N = Math.max(1, sortedBins.length || 1);
          const seg = (2 * HALF_W) / N;
          const slotInnerW = seg - 2 * PALLET_EDGE_CLEAR;
          const palletW = Math.min(PALLET_W_FT, Math.max(0.5, slotInnerW));
          sortedBins.forEach((binVal, i) => {
            const xLocal = centersX[i] ?? 0;
            const ordered = sortPlacards(placardMap.get(binVal) || []);
            const nStacks = ordered.length;
            // Instanced wireframe base on FLOOR (sit on y=0)
            if (showPalletBases && USE_INSTANCED_BASES) {
              const xScale = palletW / PALLET_W_FT;
              const baseY = THIN_BASE_T_FT / 2;
              const basePos = new THREE.Vector3(x, baseY, z).addScaledVector(rightW, xLocal);
              placeBaseInstance({
                worldX: basePos.x, worldY: baseY, worldZ: basePos.z, yawRad, xScale
              });
            }
            if (nStacks === 0) {
              if (showPalletBases && !USE_INSTANCED_BASES) {
                const pallet = makeVisualPallet({ widthFt: palletW, depthFt: PALLET_D_FT, showCargo: false });
                const p = new THREE.Vector3(x, yCenter, z).addScaledVector(rightW, xLocal);
                pallet.position.copy(p);
                root.add(pallet);
              }
            } else {
              const cargoH = chooseCargoHeight({ shelfNum: 1, nStacks, yCenter, beamLevels: [BEAM_START_FT] });
              // Build bin label text once per bin (for picking), then attach to first stack's box front
              let binLabelText = '';
              if (entry.isPicking) {
                try {
                  const infoPerShelf = entry.shelfBinSkuInfo && entry.shelfBinSkuInfo.get(1);
                  const info = infoPerShelf && infoPerShelf.get(binVal);
                  const skuText = info?.sku ? String(info.sku) : '';
                  const piecesVal = (info && Number.isFinite(Number(info.pieces))) ? Number(info.pieces) : null;
                  const clientMap = entry.clients && entry.clients.get(1);
                  const clientStr = clientMap && clientMap.get(binVal);
                  const clientFirst = clientStr ? clientStr.split(',')[0].trim() : '';
                  const multi = clientStr && clientStr.includes(',');
                  const clientLine = clientFirst ? (multi ? `${clientFirst} +` : clientFirst) : '';
                  const locMap = entry.locationLabels && entry.locationLabels.get(1);
                  const loc = locMap && locMap.get(binVal);
                  if (skuText || clientLine || loc) {
                    const skuQtyLine = skuText ? `SKU ${skuText}${piecesVal != null ? ` · Qty ${piecesVal}` : ''}` : (piecesVal != null ? `Qty ${piecesVal}` : '');
                    const lines = [ clientLine || '', skuQtyLine, loc || '' ].filter(Boolean);
                    binLabelText = lines.join('\n');
                  }
                } catch {}
              }
              ordered.forEach((pl, si) => {
                const pallet = makeVisualPallet({
                  widthFt: palletW,
                  depthFt: PALLET_D_FT,
                  showCargo: true,
                  labelText: (entry.isPicking ? (si === 0 ? binLabelText : '') : pl.label),
                  cargoHeightFt: cargoH,
                  frontIsNegZ
                });
                const yStack = yCenter + si * (baseT() + cargoH + STACK_GAP_FT);
                const p = new THREE.Vector3(x, yStack, z).addScaledVector(rightW, xLocal);
                pallet.position.copy(p);
                root.add(pallet);
              });
            }
          });
          return;
        }
        // ---- REAL RACK ----
        const rack = buildRack(x, z, RACK_D_FT, RACK_W_FT, RACK_H_FT,
                               Math.max(0, (entry.shelves?.size || 1) - 1),
                               BEAM_START_FT, BEAM_SPACING_FT, yawRad);
        root.add(rack);
        // BAY SIGN (mounted on the aisle-facing front face of this rack, centered across width)
        {
          const old = rack.getObjectByName('bay-tag');
          if (old) rack.remove(old);
          // Honor layout numbering as-is (no visual flip)
          const bayTag = makeSign(String(bay).padStart(3, '0'), 1.5, 1.0);
          bayTag.name = 'bay-tag';
          bayTag.scale.set(2, 2, 1);
          const mountY = 26;
          const STANDOFF = 0.16; // slightly farther out
          // Determine which local face (+Z or -Z) points toward the aisle front
          const aisleFront = new THREE.Vector3(0,0, frontIsNegZ ? -1 : 1);
          const dot = frontW.dot(aisleFront); // >0 means rack local +Z faces aisle front
          const localZ = (dot > 0 ? +1 : -1) * (HALF_D + STANDOFF);
          const rotY = (localZ < 0) ? Math.PI : 0; // face outward toward aisle
          bayTag.position.set(0, mountY, localZ);
          bayTag.rotation.y = rotY;
          rack.add(bayTag);
        }
        // AISLE SIGN (once) — place on both sides at the same depth as bay placards
        if (!aisleSignMounted.has(aisle)) {
          const label = `AISLE ${String(aisle).padStart(3, '0')}`;
          const mountY = 24;
          const STANDOFF = 0.16; // slightly farther out (match bay placards)
          // Decide which rack-local face is toward aisle front using the current rack yaw
          const aisleFront = new THREE.Vector3(0,0, frontIsNegZ ? -1 : 1);
          const dotFront = frontW.dot(aisleFront);
          const frontLocalZ = (dotFront > 0 ? +1 : -1) * (HALF_D + STANDOFF);
          const backLocalZ = -frontLocalZ;
          const rotFront = (frontLocalZ < 0) ? Math.PI : 0;
          const rotBack = (backLocalZ < 0) ? Math.PI : 0;
          const tagFront = makeSign(label);
          tagFront.position.set(5, mountY, frontLocalZ);
          tagFront.rotation.y = rotFront;
          rack.add(tagFront);
          const tagBack = makeSign(label);
          tagBack.position.set(5, mountY, backLocalZ);
          tagBack.rotation.y = rotBack;
          rack.add(tagBack);
          aisleSignMounted.add(aisle);
        }
        // beam levels list
        const beamLevels = [];
        const shelfCount = Math.max(1, entry.shelves?.size || 1);
        for (let i = 0; i < shelfCount - 1; i++) {
          const lvl = BEAM_START_FT + i * BEAM_SPACING_FT;
          if (lvl <= RACK_H_FT + 1e-6) beamLevels.push(lvl);
        }
        // per-shelf pallets (include floor shelf 1)
        const shelvesWithData = new Set([ ...entry.shelfBins.keys(), 1 ]);
        shelvesWithData.forEach(shelfNumRaw => {
          const shelfNum = Number(shelfNumRaw);
          const binsSet = entry.shelfBins.get(shelfNum) || new Set([1]);
          const sortedBins = [...binsSet].sort((a, b) => a - b);
          const centersX = centersFor(sortedBins.length);
          // y center per shelf
          let yCenter;
          if (shelfNum <= 1) {
            yCenter = baseT() / 2;
          } else {
            const idx = shelfNum - 2;
            if (idx >= 0 && idx < beamLevels.length) {
              const beamYft = BEAM_START_FT + idx * BEAM_SPACING_FT;
              yCenter = feet(beamYft) + (0.25 / 2) + TWO_BY_TALL_FT + (baseT() / 2);
            } else {
              yCenter = feet(BEAM_START_FT) + (baseT() / 2);
            }
          }
          shelfY.set(shelfNum, yCenter);
          shelfCenters.set(shelfNum, { bins: sortedBins.length ? sortedBins : [1], centersX });
          // width per slot
          const N = Math.max(1, sortedBins.length);
          const seg = (2 * HALF_W) / N;
          const slotInnerW = seg - 2 * PALLET_EDGE_CLEAR;
          const palletW = Math.min(PALLET_W_FT, Math.max(0.5, slotInnerW));
          const placardMap = entry.shelfBinPlacards.get(shelfNum) || new Map();
          sortedBins.forEach((binVal, i) => {
            const xLocal = centersX[i];
            const ordered = sortPlacards(placardMap.get(binVal) || []);
            const nStacks = ordered.length;
            // Instanced base on SHELF (base center at yCenter). Skip for picking bins when empty.
            if (showPalletBases && USE_INSTANCED_BASES && !(entry.isPicking && nStacks === 0)) {
              const xScale = palletW / PALLET_W_FT;
              const rackLocal = new THREE.Vector3(xLocal, 0, 0).applyAxisAngle(new THREE.Vector3(0,1,0), yawRad);
              const worldX = x + rackLocal.x;
              const worldZ = z + rackLocal.z;
              placeBaseInstance({
                worldX, worldY: yCenter, worldZ, yawRad, xScale
              });
            }
            if (nStacks === 0) {
              // For picking bins, do not render anything when slot is empty.
              if (!entry.isPicking && !USE_INSTANCED_BASES && showPalletBases) {
                // empty slot: only add base if not instancing
                const pallet = makeVisualPallet({ widthFt: palletW, depthFt: PALLET_D_FT, showCargo: false });
                pallet.position.set(xLocal, yCenter, 0);
                rack.add(pallet);
              }
            } else {
              const cargoH = chooseCargoHeight({ shelfNum, nStacks, yCenter, beamLevels });
              // Build bin label text once per bin (for picking), then attach to first stack's box front
              let binLabelText = '';
              if (entry.isPicking) {
                try {
                  const infoPerShelf = entry.shelfBinSkuInfo && entry.shelfBinSkuInfo.get(shelfNum);
                  const info = infoPerShelf && infoPerShelf.get(binVal);
                  const skuText = info?.sku ? String(info.sku) : '';
                  const qtyVal = (info && Number.isFinite(Number(info.pieces))) ? Number(info.pieces) : null;
                  const clientMap = entry.clients && entry.clients.get(shelfNum);
                  const clientStr = clientMap && clientMap.get(binVal);
                  const clientFirst = clientStr ? clientStr.split(',')[0].trim() : '';
                  const multi = clientStr && clientStr.includes(',');
                  const clientLine = clientFirst ? (multi ? `${clientFirst} +` : clientFirst) : '';
                  const locMap = entry.locationLabels && entry.locationLabels.get(shelfNum);
                  const loc = locMap && locMap.get(binVal);
                  if (skuText || clientLine || loc) {
                    const skuQtyLine = skuText
                      ? `SKU ${skuText}${qtyVal != null ? ` · Qty ${qtyVal}` : ''}`
                      : (qtyVal != null ? `Qty ${qtyVal}` : '');
                    const lines = [ clientLine || '', skuQtyLine, loc || '' ].filter(Boolean);
                    binLabelText = lines.join('\n');
                  }
                } catch {}
              }
              ordered.forEach((pl, si) => {
                const pallet = makeVisualPallet({
                  widthFt: palletW,
                  depthFt: PALLET_D_FT,
                  showCargo: true,
                  // Attach bin label only to the first stack when picking; else normal placard
                  labelText: (entry.isPicking ? (si === 0 ? binLabelText : '') : pl.label),
                  cargoHeightFt: cargoH,
                  frontIsNegZ
                });
                const yStack = yCenter + si * (baseT() + cargoH + STACK_GAP_FT);
                pallet.position.set(xLocal, yStack, 0);
                rack.add(pallet);
              });
            }
          });
        });
      });
    });
    // finalize instanced bases
    if (baseInst) {
      baseInst.count = baseIdx;
      baseInst.instanceMatrix.needsUpdate = true;
    }
  }
  // --- Render purely from design map (no rows required) ---
  function renderDesignOnly(root) {
    const L = Math.max(10, Number(dims?.lengthFt ?? 200));
    const W = Math.max(10, Number(dims?.widthFt ?? 120));
    const toWorld = ({ xFt, zFt }) => {
      if ((origin || '').toLowerCase() === 'centered') return { x: xFt, z: zFt };
      return { x: xFt - L / 2, z: zFt - W / 2 };
    };
    const aisleSignMounted = new Set();
    const aisleMap = new Map();
    designMapRef.current.forEach((val, key) => {
      const [aStr, bStr] = key.split('|');
      const a = Number(aStr), b = Number(bStr);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return;
      if (selectedAisleSet && !selectedAisleSet.has(a)) return;
      if (!aisleMap.has(a)) aisleMap.set(a, []);
      aisleMap.get(a).push({ bay: b, ...val });
    });
    const aisles = [...aisleMap.keys()].sort((a,b)=>a-b);
    aisles.forEach(aisle => {
      const bays = aisleMap.get(aisle).sort((p,q)=>p.bay-q.bay);
      // Compute aisle bounds and decide front side (negZ vs posZ) using bay world positions
      let minX = Infinity, maxX = -Infinity, minEdgeZ = Infinity, maxEdgeZ = -Infinity;
      let minBay = null, maxBay = null, minBayZ = 0, maxBayZ = 0;
      bays.forEach(({ bay, xFt, zFt }) => {
        const { x, z } = toWorld({ xFt, zFt });
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        const negEdge = z - HALF_D;
        const posEdge = z + HALF_D;
        minEdgeZ = Math.min(minEdgeZ, negEdge);
        maxEdgeZ = Math.max(maxEdgeZ, posEdge);
        if (minBay == null || bay < minBay) { minBay = bay; minBayZ = z; }
        if (maxBay == null || bay > maxBay) { maxBay = bay; maxBayZ = z; }
      });
      // Determine aisle facing strictly from persisted meta; default to -Z if unknown
      const persistedFacing = aisleFacingRef.current?.get?.(aisle);
      const frontIsNegZ = (typeof persistedFacing === 'boolean') ? persistedFacing : true;
      bays.forEach(({ bay, xFt, zFt, oriDeg }) => {
        const { x, z } = toWorld({ xFt, zFt });
        const yawRad = THREE.MathUtils.degToRad(oriDeg || 0);
        const frontW = new THREE.Vector3(0,0,-1).applyAxisAngle(new THREE.Vector3(0,1,0), yawRad);
        const rack = buildRack(
          x, z,
          RACK_D_FT, RACK_W_FT, RACK_H_FT,
          0, BEAM_START_FT, BEAM_SPACING_FT,
          yawRad
        );
        root.add(rack);
        // index for focusLocation
        locIndexRef.current.set(`${aisle}|${bay}`, { x, z, yawRad, shelfBins: new Map() });
        if (!aisleSignMounted.has(aisle)) {
          const label = `AISLE ${String(aisle).padStart(3,'0')}`;
          const STANDOFF = 0.16; // slightly farther out (match bay placards)
          // Mount on both faces at same depth as bay placards; use this rack's yaw to choose face
          const aisleFront = new THREE.Vector3(0,0, frontIsNegZ ? -1 : 1);
          const dotFront = frontW.dot(aisleFront);
          const frontLocalZ = (dotFront > 0 ? +1 : -1) * (HALF_D + STANDOFF);
          const backLocalZ = -frontLocalZ;
          const rotFront = (frontLocalZ < 0) ? Math.PI : 0;
          const rotBack = (backLocalZ < 0) ? Math.PI : 0;
          const tagFront = makeSign(label);
          tagFront.position.set(-HALF_W + 0.22, 12, frontLocalZ);
          tagFront.rotation.y = rotFront;
          rack.add(tagFront);
          const tagBack = makeSign(label);
          tagBack.position.set(-HALF_W + 0.22, 12, backLocalZ);
          tagBack.rotation.y = rotBack;
          rack.add(tagBack);
          aisleSignMounted.add(aisle);
        }
      });
    });
  }
  // ----------------------------------------------------------
  // init / teardown
  // ----------------------------------------------------------
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0f0f10);
    sceneRef.current = scene;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.shadowMap.enabled = false;
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 5000);
    camera.position.set(60, 40, 80);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.target.set(0, 6, 0); // slightly lower pivot for closer zoom
    controls.enableZoom = true;
    controls.zoomSpeed = 1.25;
    controls.minDistance = 0.25; // allow even closer zoom
    controls.maxDistance = 5000;
    controls.minPolarAngle = THREE.MathUtils.degToRad(1);   // allow lower angles
    controls.maxPolarAngle = THREE.MathUtils.degToRad(89.5);
    controls.enablePan = true;
    controlsRef.current = controls;
    // lights
    const amb = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(amb);
    const dir = new THREE.DirectionalLight(0xffffff, 0.65);
    dir.position.set(30, 50, 30);
    scene.add(dir);
    // Stick figure walker (only visible in observer mode)
    const makeStickMan = () => {
      const g = new THREE.Group();
      const color = 0x00e0c2;
      const mat = new THREE.MeshBasicMaterial({ color });
      // Head
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.45, 18, 18), mat);
      head.position.set(0, 6.0, 0);
      g.add(head);
      // Torso
      const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.45, 2.2, 12), mat);
      torso.position.set(0, 4.5, 0);
      g.add(torso);
      // Upper legs
      const legL = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 1.6, 10), mat);
      legL.position.set(-0.4, 3.0, 0);
      const legR = legL.clone(); legR.position.x = 0.4;
      g.add(legL, legR);
      // Lower legs
      const shinL = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 1.4, 10), mat);
      shinL.position.set(-0.4, 1.9, 0);
      const shinR = shinL.clone(); shinR.position.x = 0.4;
      g.add(shinL, shinR);
      // Arms
      const armL = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 1.6, 10), mat);
      armL.position.set(-0.9, 4.8, 0);
      const armR = armL.clone(); armR.position.x = 0.9;
      g.add(armL, armR);
      // Forearms
      const foreL = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 1.4, 10), mat);
      foreL.position.set(-0.9, 3.9, 0);
      const foreR = foreL.clone(); foreR.position.x = 0.9;
      g.add(foreL, foreR);
      g.visible = false;
      return g;
    };
    const walker = makeStickMan();
    scene.add(walker);
    walker.position.set(0, 0, 0);
    walkerMeshRef.current = walker;
    // create shell & content
    rebuildShell();
    rebuildScene();
    invalidate();
    // keyboard walk helpers
    const forward = new THREE.Vector3();
    const sideways = new THREE.Vector3();
    const L = Math.max(10, Number(dims?.lengthFt ?? 200));
    const W = Math.max(10, Number(dims?.widthFt ?? 120));
    const baseStep = Math.max(2, Math.min(14, Math.round(Math.max(L, W) / 40)));
    const sprintMult = 2.5;
    const moveAlongLook = (sign, mult = 1) => {
      const cam = cameraRef.current;
      const ctr = controlsRef.current?.target;
      if (!cam || !ctr) return;
      cam.getWorldDirection(forward);
      forward.y = 0;
      if (forward.lengthSq() === 0) return;
      forward.normalize();
      const step = sign * baseStep * mult;
      cam.position.addScaledVector(forward, step);
      ctr.addScaledVector(forward, step);
      clampViewYOnly();
      invalidate();
    };
    const moveSideways = (sign, mult = 1) => {
      const cam = cameraRef.current;
      const ctr = controlsRef.current?.target;
      if (!cam || !ctr) return;
      cam.getWorldDirection(forward);
      forward.y = 0;
      if (forward.lengthSq() === 0) return;
      forward.normalize();
      sideways.crossVectors(new THREE.Vector3(0, 1, 0), forward).normalize();
      const step = sign * baseStep * mult;
      cam.position.addScaledVector(sideways, step);
      ctr.addScaledVector(sideways, step);
      clampViewYOnly();
      invalidate();
    };
    const walkState = { shift: false };
    // crouch helper (hold C)
    const crouchState = { active: false, savedY: null, savedTargetY: null };
    const onKeyDown = (e) => {
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.key === 'Shift') walkState.shift = true;
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        if (!crouchState.active) {
          const cam = cameraRef.current;
          const ctr = controlsRef.current?.target;
          if (cam && ctr) {
            crouchState.active = true;
            crouchState.savedY = cam.position.y;
            crouchState.savedTargetY = ctr.y;
            ctr.y = Math.max(0.05, Math.min(ctr.y, 0.35));
            cam.position.y = Math.max(0.15, Math.min(cam.position.y, ctr.y + 0.25));
            invalidate();
          }
        }
      }
      const mult = walkState.shift ? sprintMult : 1;
      if (e.key === 'ArrowUp' || e.key.toLowerCase() === 'w') { e.preventDefault(); moveAlongLook(+1, mult); }
      else if (e.key === 'ArrowDown' || e.key.toLowerCase() === 's') { e.preventDefault(); moveAlongLook(-1, mult); }
      else if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') { e.preventDefault(); moveSideways(+1, mult); }
      else if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') { e.preventDefault(); moveSideways(-1, mult); }
    };
    const onKeyUp = (e) => {
      if (e.key === 'Shift') walkState.shift = false;
      if (e.key === 'c' || e.key === 'C') {
        const cam = cameraRef.current;
        const ctr = controlsRef.current?.target;
        if (crouchState.active && cam && ctr) {
          if (crouchState.savedY != null) cam.position.y = crouchState.savedY;
          if (crouchState.savedTargetY != null) ctr.y = crouchState.savedTargetY;
          crouchState.active = false; crouchState.savedY = null; crouchState.savedTargetY = null;
          invalidate();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp, { passive: true });
    // render-on-demand loop
    let stopped = false;
    const loop = () => {
      if (stopped) return;
      controls.update();
      clampViewYOnly();
      // fly-to tween here
      if (isAnimatingRef.current) {
        const cam = cameraRef.current;
        const ctr = controlsRef.current.target;
        const {
          start, duration,
          fromPos, toPos,
          fromTgt, toTgt,
          fromFov, wideFov, toFov, hasZoomPop
        } = animStateRef.current;
        const now = performance.now();
        const t = Math.min(1, (now - start) / duration);
        const k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        cam.position.lerpVectors(fromPos, toPos, k);
        ctr.lerpVectors(fromTgt, toTgt, k);
        // Ensure camera actually faces target even while controls are disabled
        cam.lookAt(ctr.x, ctr.y, ctr.z);
        if (hasZoomPop) {
          const split = 0.3;
          if (t < split) {
            const kt = t / split;
            cam.fov = THREE.MathUtils.lerp(fromFov, wideFov, kt);
          } else {
            const kt = (t - split) / (1 - split);
            cam.fov = THREE.MathUtils.lerp(wideFov, toFov, kt);
          }
        } else {
          cam.fov = THREE.MathUtils.lerp(fromFov, toFov, k);
        }
        cam.updateProjectionMatrix();
        needsRenderRef.current = true;
        if (t >= 1) {
          isAnimatingRef.current = false;
          controls.enabled = true;
          controls.update();
        }
      }
      // Walk animation (spline-based)
      if (walkRef.current.active) {
        const cam = cameraRef.current;
        const ctr = controlsRef.current.target;
        const nowTs = performance.now();
        if (!lastFrameTimeRef.current) lastFrameTimeRef.current = nowTs;
        let dt = (nowTs - lastFrameTimeRef.current) / 1000;
        lastFrameTimeRef.current = nowTs;
        dt = Math.max(0.0, Math.min(0.05, dt));
        const { curve, totalLen, speed, eyeY, finalTarget, finalPos } = walkRef.current;
        const tCurrent = walkRef.current.traveled / totalLen;
        const edgeEase = 0.35 + 0.65 * Math.sin(Math.PI * tCurrent);
        let effectiveSpeed = speed * edgeEase;
        let traveled = walkRef.current.traveled + effectiveSpeed * dt;
        if (traveled >= totalLen) {
          traveled = totalLen;
          walkRef.current.active = false;
          if (observerRef.current.active) {
            const finalXZ = new THREE.Vector3(finalPos.x, 0, finalPos.z);
            if (walkerMeshRef.current) {
              walkerMeshRef.current.position.set(finalXZ.x, 0, finalXZ.z);
              walkerMeshRef.current.visible = false;
            }
            const distObs = 60, elevObs = 16;
            const dirObs = new THREE.Vector3().subVectors(finalTarget, finalXZ).normalize();
            const camPos = new THREE.Vector3().copy(finalXZ).addScaledVector(dirObs, -distObs);
            cam.position.set(camPos.x, elevObs, camPos.z);
            cam.lookAt(finalTarget.x, finalTarget.y, finalTarget.z);
            observerRef.current.active = false;
            controls.enabled = true;
          } else {
            animStateRef.current.start = performance.now();
            animStateRef.current.duration = 700;
            animStateRef.current.fromPos.copy(cam.position);
            animStateRef.current.toPos.copy(finalPos);
            animStateRef.current.fromTgt.copy(ctr);
            animStateRef.current.toTgt.copy(finalTarget);
            animStateRef.current.fromFov = cam.fov;
            animStateRef.current.wideFov = cam.fov;
            animStateRef.current.toFov = 48;
            animStateRef.current.hasZoomPop = false;
            controls.enabled = false;
            isAnimatingRef.current = true;
          }
        }
        walkRef.current.traveled = traveled;
        const t = traveled / totalLen;
        const pos = curve.getPointAt(t);
        const tang = curve.getTangentAt(t).normalize();
        const yaw = Math.atan2(tang.x, tang.z);
        if (observerRef.current.active) {
          walkRef.current.currXZ.copy(pos);
          if (walkerMeshRef.current) {
            walkerMeshRef.current.position.set(pos.x, 0, pos.z);
            walkerMeshRef.current.rotation.y = yaw;
            // walking bobbing
            const w = Math.max(0.5, Math.min(3.0, effectiveSpeed / 6));
            walkerAnimRef.current.phase += w * dt * Math.PI * 2;
            const bob = Math.abs(Math.sin(walkerAnimRef.current.phase)) * 0.15;
            walkerMeshRef.current.position.y = bob;
          }
          const dist = 60; const elev = 16;
          const dir = tang.clone();
          const camPos = new THREE.Vector3().copy(pos).addScaledVector(dir, -dist);
          cam.position.set(camPos.x, elev, camPos.z);
          cam.lookAt(pos.x, 3.0, pos.z);
        } else {
          cam.position.set(pos.x, eyeY, pos.z);
          // look ahead along curve
          const lookDelta = 0.015;
          let tLook = Math.min(1, t + lookDelta);
          let lookPos = curve.getPointAt(tLook);
          ctr.set(lookPos.x, eyeY, lookPos.z);
          cam.lookAt(ctr.x, ctr.y, ctr.z);
        }
        cam.updateProjectionMatrix();
        needsRenderRef.current = true;
      }
      // ceiling toggle (if used)
      const cam = cameraRef.current;
      const ceil = ceilingMeshRef.current;
      if (cam && ceil) {
        const visible = cam.position.y <= CEILING_H_FT + 0.1;
        if (ceil.visible !== visible) {
          ceil.visible = visible;
          invalidate();
        }
      }
      if (needsRenderRef.current) renderOnce();
      requestAnimationFrame(loop);
    };
    loop();
    // cause renders when the user interacts or damping changes
    controls.addEventListener('change', invalidate);
    // resize
    const onResize = () => {
      if (!rendererRef.current || !cameraRef.current) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      rendererRef.current.setSize(w, h);
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      invalidate();
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);
    window.addEventListener('resize', onResize);
    return () => {
      stopped = true;
      controls.removeEventListener('change', invalidate);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (rendererRef.current) {
        mount.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
      scene.traverse(disposeNode);
      textureBucketRef.current.forEach(t => t.dispose());
      textureBucketRef.current.clear();
      labelTextureCacheRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Rebuild when dims change
  useEffect(() => {
    if (!sceneRef.current) return;
    rebuildShell();
    invalidate();
  }, [dims]);
  // Rebuild when rows/design change
  useEffect(() => {
    if (!sceneRef.current) return;
    rebuildScene();
    invalidate();
  }, [rows, designTick, origin, selectedAisleSet, showPalletBases, palletBaseMode]);
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mountRef} className="w3d-canvas" style={{ width: '100%', height: '100%' }} />
      <div
        aria-hidden
        style={{
          position: 'absolute', left: 10, bottom: 10,
          width: 72, height: 72, borderRadius: 40,
          background: 'rgba(0,0,0,0.35)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          font: 'bold 13px system-ui',
          userSelect: 'none', pointerEvents: 'none',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.15) inset'
        }}
        title="Compass"
      >
        <div style={{ position: 'relative', width: 62, height: 62 }}>
          <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)' }}>N</div>
          <div style={{ position: 'absolute', bottom: -1, left: '50%', transform: 'translateX(-50%)' }}>S</div>
          <div style={{ position: 'absolute', left: -1, top: '50%', transform: 'translateY(-50%)' }}>W</div>
          <div style={{ position: 'absolute', right: -1, top: '50%', transform: 'translateY(-50%)' }}>E</div>
          <div style={{ position: 'absolute', left: '50%', top: '50%', width: 2, height: 30, background: 'rgba(255,255,255,0.6)', transform: 'translate(-50%,-50%)' }} />
          <div style={{ position: 'absolute', left: '50%', top: '50%', width: 30, height: 2, background: 'rgba(255,255,255,0.6)', transform: 'translate(-50%,-50%)' }} />
        </div>
      </div>
    </div>
  );
});
